import { useState } from 'react';
import { Undo2 } from 'lucide-react';
import ModalShell from '@/components/ModalShell';
import { reinstateDepositWithReason } from '@/domain/depositFlow';
import { forfeitedAud } from '@/domain/depositStatus';
import { useUIStore } from '@/state/uiStore';
import { aud } from '@/utils/format';
import type { Deposit } from '@/domain/types';

/**
 * Reinstate a forfeited deposit (B4, 75ng; Anas 1 Oct Q3): Ops or the owner,
 * and always with a typed reason. The reason and who lands on the deposit's
 * movement history and the activity feed; the money goes back on file for 12
 * months, where the Sweeper can place it. It used to reinstate on one click with
 * no reason ("Reinstated by management").
 */
export default function ReinstateDepositModal({
  deposit,
  clientName,
  onClose,
}: {
  deposit: Deposit | null;
  clientName: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const trimmed = reason.trim();

  const close = () => {
    setReason('');
    setError(null);
    onClose();
  };

  const confirm = async () => {
    if (!deposit || !trimmed || saving) return;
    setSaving(true);
    setError(null);
    const res = await reinstateDepositWithReason(deposit.id, trimmed);
    setSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    pushNotification({
      intensity: 'info',
      title: 'Deposit reinstated',
      body: `${clientName}'s ${aud(forfeitedAud(deposit))} is back on file for the Sweeper to place.`,
      ttlMs: 4000,
    });
    close();
  };

  return (
    <ModalShell open={!!deposit} onClose={close} title="Reinstate deposit" testId="deposit-reinstate-modal">
      {deposit ? (
        <div className="space-y-3">
          <p className="font-serif text-sm text-cream-soft">
            Give {clientName}&rsquo;s forfeited {aud(forfeitedAud(deposit))} back. It goes back on file for 12 months,
            for the Sweeper to place. Your reason is kept on the deposit&rsquo;s history.
          </p>
          <label className="block">
            <span className="label-small-caps">Why (typed, required)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="deposit-reinstate-reason"
              placeholder="What happened, in a line"
              className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            />
          </label>
          {error ? (
            <p className="text-xs text-blood" data-testid="deposit-reinstate-error">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={close} className="btn-ghost text-xs">
              Back
            </button>
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={!trimmed || saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-azure/80 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-black hover:bg-azure disabled:opacity-40"
              data-testid="deposit-reinstate-confirm"
            >
              <Undo2 size={12} /> {saving ? 'Reinstating…' : 'Reinstate'}
            </button>
          </div>
        </div>
      ) : null}
    </ModalShell>
  );
}
